import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatMoney, formatApiError } from "../lib/api";
import PriceDisplay from "../components/PriceDisplay";
import { carImageUrl } from "../components/cars/CarCard";
import { useCurrency } from "../context/CurrencyContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Minus, Plus } from "lucide-react";

export default function GuestNegotiationPage() {
  const { negId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { currencies } = useCurrency();

  const [neg, setNeg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "accepted" | "rejected"
  const [counterPrice, setCounterPrice] = useState(0);

  useEffect(() => {
    if (!negId || !token) { setLoading(false); return; }
    api.get(`/guest/negotiate/${negId}?token=${token}`)
      .then(({ data }) => {
        setNeg(data);
        const initialStep = Math.max(500, Math.round((data.listing_price || 0) * 0.05));
        const ownHist = (data.history || []).filter((h) => h.by === "customer");
        const ownLast = ownHist.length ? ownHist[ownHist.length - 1].price : null;
        const floorPrice = ownLast ?? (data.current_offer || 0);
        setCounterPrice(Math.min(data.current_offer || 0, floorPrice + initialStep));
      })
      .catch(() => toast.error("Negotiation not found or link expired."))
      .finally(() => setLoading(false));
  }, [negId, token]);

  const step = neg ? Math.max(500, Math.round((neg.listing_price || 0) * 0.05)) : 1000;

  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "accept") {
        await api.post(`/negotiations/${negId}/accept`, {}, {
          headers: { "X-Guest-Token": token, "X-Guest-Neg-Id": negId }
        });
        toast.success("Offer accepted! Redirecting to payment...");
        const checkoutUrl = `/guest/checkout?car_id=${neg.car_id}&start_date=${neg.start_date}&end_date=${neg.end_date}&agreed_price=${neg.current_offer}&neg_id=${negId}&token=${token}`;
        console.log("Redirecting to:", checkoutUrl, "neg:", JSON.stringify(neg));
        window.location.href = checkoutUrl;
        return;
      } else if (action === "reject") {
        await api.post(`/negotiations/${negId}/reject`, {}, {
          headers: { "X-Guest-Token": token, "X-Guest-Neg-Id": negId }
        });
        setDone("rejected");
        toast.info("Offer rejected.");
      } else if (action === "counter") {
        const res = await api.post(`/negotiations/${negId}/counter`,
          { counter_price: counterPrice },
          { headers: { "X-Guest-Token": token, "X-Guest-Neg-Id": negId } }
        );
        setNeg(res.data);
        toast.success("Counter offer sent! The owner will respond within 24 hours.");
      }
    } catch(e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-xl mx-auto py-20 text-center text-muted-foreground"><Loader2 className="animate-spin mx-auto" size={32} /></div></PublicLayout>;

  if (!neg) return <PublicLayout>
    <div className="max-w-xl mx-auto py-20 text-center">
      <XCircle className="mx-auto text-destructive mb-4" size={42} />
      <h1 className="font-display text-2xl font-semibold">Link expired or invalid</h1>
      <p className="text-muted-foreground mt-2">This negotiation link may have expired. Please contact us at info@rydexpress.com.</p>
    </div>
  </PublicLayout>;

  if (done === "accepted") return <PublicLayout>
    <div className="max-w-xl mx-auto py-20 text-center px-4">
      <CheckCircle2 className="mx-auto text-primary mb-4" size={42} />
      <h1 className="font-display text-2xl font-semibold">Offer accepted!</h1>
      <p className="text-muted-foreground mt-2 mb-6">The agreed price is <strong><PriceDisplay amount={neg.current_offer} listingCurrency={neg.currency} /></strong> total for {neg.days} day(s).</p>
      <Button asChild className="bg-primary hover:bg-primary/90">
        <Link to={`/guest/checkout?car_id=${neg.car_id}&start_date=${neg.start_date}&end_date=${neg.end_date}&agreed_price=${neg.current_offer}`}>
          Proceed to pay booking fee
        </Link>
      </Button>
    </div>
  </PublicLayout>;

  if (done === "rejected") return <PublicLayout>
    <div className="max-w-xl mx-auto py-20 text-center px-4">
      <XCircle className="mx-auto text-destructive mb-4" size={42} />
      <h1 className="font-display text-2xl font-semibold">Offer rejected</h1>
      <p className="text-muted-foreground mt-2">You can browse other available cars.</p>
      <Button asChild className="mt-6 bg-primary hover:bg-primary/90"><Link to="/cars">Browse cars</Link></Button>
    </div>
  </PublicLayout>;

  const isExpired = neg.status === "expired" || neg.status === "rejected" || neg.status === "accepted";
  const canAct = neg.status === "pending_customer";

  return (
    <PublicLayout>
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold mb-2">Price Negotiation</div>
          <div className="flex items-center gap-3">
            {neg.car_image && (
              <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted">
                <img src={carImageUrl(neg.car_image)} alt={neg.car_title} className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <h1 className="font-display text-2xl font-semibold">{neg.car_title}{neg.car_year ? ` (${neg.car_year})` : ""}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {neg.start_date}{neg.start_time ? ` ${neg.start_time}` : ""} → {neg.end_date}{neg.end_time ? ` ${neg.end_time}` : ""} · {neg.days} day{neg.days > 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        <Card className="border-border shadow-organic p-6 mb-4">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Current offer</div>
            <div className="font-display text-3xl font-bold"><PriceDisplay amount={neg.current_offer} listingCurrency={neg.currency} /></div>
            <div className="text-xs text-muted-foreground mt-1">Listed total: <PriceDisplay amount={neg.listing_price} listingCurrency={neg.currency} /></div>
            <div className="mt-2">
              {neg.status === "pending_owner" && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">Waiting for owner response</span>}
              {neg.status === "pending_customer" && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">Owner countered — your turn</span>}
              {neg.status === "accepted" && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Accepted</span>}
              {neg.status === "rejected" && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">Rejected</span>}
              {neg.status === "expired" && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">Expired</span>}
            </div>
          </div>
        </Card>

        {canAct && (
          <Card className="border-border shadow-organic p-6 space-y-4">
            <div className="text-sm font-semibold text-center">Your response</div>

            <div className="flex items-center justify-center gap-4">
              <button type="button" onClick={() => {
                    const ownHist = (neg.history || []).filter((h) => h.by === "customer");
                    const floorPrice = ownHist.length ? ownHist[ownHist.length - 1].price : neg.current_offer;
                    setCounterPrice(p => Math.max(floorPrice, p - step));
                  }}
                disabled={busy} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40">
                <Minus size={16} />
              </button>
              <div className="font-display text-2xl font-bold min-w-[160px] text-center">
                <PriceDisplay amount={counterPrice} listingCurrency={neg.currency} />
              </div>
              <button type="button" onClick={() => setCounterPrice(p => p + step)}
                disabled={busy} className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 disabled:opacity-40">
                <Plus size={16} />
              </button>
            </div>
            <div className="text-xs text-center text-muted-foreground">
              Rounds used: {neg.rounds_used} / {neg.max_rounds}
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={() => act("accept")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 size={14} className="mr-2" /> Accept <PriceDisplay amount={neg.current_offer} listingCurrency={neg.currency} />
              </Button>
              <Button onClick={() => act("counter")} disabled={busy || counterPrice > neg.current_offer || counterPrice < ((neg.history || []).filter((h) => h.by === "customer").slice(-1)[0]?.price ?? neg.current_offer)}
                variant="outline" className="border-[#E97A48] text-[#E97A48] hover:bg-[#FCE9DC]">
                Counter with <PriceDisplay amount={counterPrice} listingCurrency={neg.currency} />
              </Button>
              <Button onClick={() => act("reject")} disabled={busy} variant="ghost" className="text-destructive hover:bg-destructive/10">
                <XCircle size={14} className="mr-2" /> Reject offer
              </Button>
            </div>
          </Card>
        )}

        {isExpired && (
          <Card className="border-border p-6 text-center">
            <p className="text-muted-foreground text-sm">This negotiation has ended.</p>
            <Button asChild className="mt-4 bg-primary hover:bg-primary/90"><Link to="/cars">Browse cars</Link></Button>
          </Card>
        )}
      </div>
    </PublicLayout>
  );
}
